import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Mail, Play, Pause, Loader2, Lock, AudioLines, Download } from "lucide-react";
import { listenInfo, listenAccess, listenAudioUrl, letterPhotoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NoIndex } from "../components/Seo";

const Player = ({ src, testid }) => {
  const ref = useRef(null);
  const [state, setState] = useState("idle");
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const a = new Audio(src);
    ref.current = a;
    a.onended = () => { setState("idle"); setPct(100); };
    a.ontimeupdate = () => a.duration && setPct((a.currentTime / a.duration) * 100);
    a.onwaiting = () => setState("loading");
    a.onplaying = () => setState("playing");
    return () => { a.pause(); ref.current = null; };
  }, [src]);
  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    if (state === "playing") { a.pause(); setState("paused"); return; }
    setState("loading");
    try { await a.play(); setState("playing"); } catch { setState("idle"); }
  };
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Play;
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <button type="button" onClick={toggle} data-testid="listen-play-btn" aria-label={state === "playing" ? "Pause" : "Play"} className="h-16 w-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-lg hover:scale-105 transition-transform">
        <Icon className={`h-7 w-7 ${state === "loading" ? "animate-spin" : ""} ${Icon === Play ? "ml-1" : ""}`} />
      </button>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export default function Listen() {
  const { linkId } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    listenInfo(linkId).then((i) => { setInfo(i); if (!i.needs_code) open(""); }).catch((e) => setErr(e?.response?.data?.detail || "This link isn't available"));
  }, [linkId]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (c) => {
    setBusy(true); setErr("");
    try { setData(await listenAccess(linkId, c)); } catch (e) { setErr(e?.response?.data?.detail || "Couldn't open the letter"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="listen-page">
      <NoIndex title="Listen" />
      <main className="max-w-xl mx-auto px-6 py-14">
        <div className="text-[10px] uppercase tracking-widest font-mono text-primary flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> A letter, kept in HearthVault</div>
        {err && !data && <div className="mt-6 rounded-2xl border border-border bg-card p-6 text-sm" data-testid="listen-error">{err}</div>}
        {info && !data && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="listen-gate">
            <h1 className="font-serif text-3xl sm:text-4xl">For {info.to_name}{info.title ? ` — ${info.title}` : ""}</h1>
            <p className="text-sm text-muted-foreground">{info.from_name ? `From ${info.from_name}. ` : ""}{info.own_voice ? "Read in their own voice." : "Read aloud."} Shared with {info.recipient_name}.</p>
            {info.needs_code && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Enter the 6-digit code you were given</div>
                <div className="flex gap-2">
                  <Input data-testid="listen-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="font-mono text-lg tracking-widest" onKeyDown={(e) => e.key === "Enter" && open(code)} />
                  <Button data-testid="listen-open-btn" className="rounded-full" disabled={busy || code.length !== 6} onClick={() => open(code)}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open"}</Button>
                </div>
              </div>
            )}
            {!info.needs_code && busy && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Opening…</div>}
          </div>
        )}
        {data && (
          <article className="mt-4 rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-6 paper-card" data-testid="listen-letter">
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl">To {data.to_name}</h1>
              {data.title && <div className="text-sm text-muted-foreground mt-1">{data.title}</div>}
            </div>
            <Player src={listenAudioUrl(linkId, data.token)} testid="listen-player" />
            <a href={`${listenAudioUrl(linkId, data.token)}&dl=1`} download className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="listen-download"><Download className="h-3.5 w-3.5" /> Download to keep</a>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1"><AudioLines className="h-3.5 w-3.5" /> {data.own_voice ? `Read in ${data.from_name ? `${data.from_name}'s` : "their"} own voice` : "Read aloud"}{data.written_at ? ` · written ${new Date(data.written_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}` : ""}</div>
            <div className="whitespace-pre-wrap font-serif text-lg leading-relaxed" data-testid="listen-body">{data.body}</div>
            {data.photos?.length > 0 && (
              <div className="grid grid-cols-2 gap-3" data-testid="listen-photos">
                {data.photos.map((p) => <a key={p.id} href={letterPhotoUrl(data.letter_id, p.id, data.token)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-muted aspect-[4/3]" data-testid={`listen-photo-${p.id}`}><img src={letterPhotoUrl(data.letter_id, p.id, data.token)} alt={p.name} className="h-full w-full object-cover hover:scale-[1.02] transition-transform duration-300" /></a>)}
              </div>
            )}
            {data.from_name && <div className="font-serif text-lg">— {data.from_name}</div>}
          </article>
        )}
        <p className="mt-8 text-[11px] text-muted-foreground">This private link was shared by the family. It expires and can be revoked at any time.</p>
      </main>
    </div>
  );
}
